import { useState } from 'react';
import { IconButton, Stack, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { useMap, AdvancedMarker } from '@vis.gl/react-google-maps';
import { useTranslation } from 'react-i18next';

interface MapControlsProps {
  bottomOffset?: number;
}

export const MapControls = ({ bottomOffset = 24 }: MapControlsProps) => {
  const map = useMap();
  const { t } = useTranslation();
  const [userPos, setUserPos] = useState<google.maps.LatLngLiteral | null>(null);
  const [locating, setLocating] = useState(false);

  const handleLocate = () => {
    if (!navigator.geolocation || !map) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        setUserPos(coords);
        map.panTo(coords);
        const z = map.getZoom() ?? 13;
        if (z < 14) map.setZoom(15);
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const zoomIn = () => {
    if (!map) return;
    const z = map.getZoom() ?? 13;
    map.setZoom(z + 1);
  };

  const zoomOut = () => {
    if (!map) return;
    const z = map.getZoom() ?? 13;
    map.setZoom(z - 1);
  };

  return (
    <>
      {userPos && (
        <AdvancedMarker position={userPos} zIndex={1000}>
          <div
            style={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              backgroundColor: '#3B82F6',
              border: '3px solid white',
              boxShadow: '0 0 0 4px rgba(59,130,246,0.25)',
            }}
          />
        </AdvancedMarker>
      )}
      <Stack
        spacing={1}
        sx={{
          position: 'absolute',
          right: 16,
          bottom: bottomOffset,
          zIndex: 5,
        }}
      >
        <Tooltip title={t('mapPage.locateMe')} placement="left">
          <IconButton onClick={handleLocate} sx={fabSx} disabled={locating}>
            <MyLocationIcon
              sx={{
                color: locating ? 'grey.500' : 'green.main',
                fontSize: 22,
              }}
            />
          </IconButton>
        </Tooltip>
        <Stack
          sx={{
            backgroundColor: 'rgba(20,20,20,0.92)',
            backdropFilter: 'blur(12px)',
            borderRadius: '14px',
            border: '1px solid',
            borderColor: 'grey.800',
            overflow: 'hidden',
            boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
          }}
        >
          <Tooltip title={t('mapPage.zoomIn')} placement="left">
            <IconButton onClick={zoomIn} sx={fabInnerSx}>
              <AddIcon sx={{ color: 'common.white', fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <div
            style={{
              height: 1,
              backgroundColor: 'rgba(255,255,255,0.08)',
            }}
          />
          <Tooltip title={t('mapPage.zoomOut')} placement="left">
            <IconButton onClick={zoomOut} sx={fabInnerSx}>
              <RemoveIcon sx={{ color: 'common.white', fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </>
  );
};

const fabSx = {
  width: 44,
  height: 44,
  backgroundColor: 'rgba(20,20,20,0.92)',
  backdropFilter: 'blur(12px)',
  borderRadius: '14px',
  border: '1px solid',
  borderColor: 'grey.800',
  boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
  '&:hover': {
    backgroundColor: 'rgba(30,30,30,0.92)',
    borderColor: 'grey.700',
  },
};

const fabInnerSx = {
  width: 44,
  height: 44,
  borderRadius: 0,
  '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
};
